import { makeAutoObservable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

export type ErrorType =
    | 'invalid-data'
    | 'minimum'
    | 'limit-reached'
    | 'deposits-not-allowed'
    | 'declined'
    | 'missing-fields'
    | 'failed-deposit-restriction'
    | 'limit-deposit-restriction'
    | 'kyc-deposit-restriction'
    | 'backoffice-deposit-restriction'
    | 'unknown';

export interface ErrorMessageType {
    type: ErrorType;
    message: string;
}

export class DepositErrorMessagesState {
    public errors: ErrorMessageType[] = [];

    private readonly configComponents: ConfigComponents;
    private readonly accountHelperMail: string;

    public constructor(
        private readonly common: Common,
        private readonly languagesState: LanguagesState
    ) {
        this.configComponents = ConfigComponents.get(this.common);
        this.accountHelperMail = this.configComponents.config.accountHelperMail;

        makeAutoObservable(this);
    }

    public clearErrorMessage = (): void => {
        this.errors = [];
    };

    public createErrorMessage = (errorType?: string, debugDetails?: string, requiredFields?: Array<string>): void => {
        switch (errorType) {
            case 'invalid-data':
                this.errors.push({
                    type: 'invalid-data',
                    message: this.languagesState.getTranslation('errors.invalid.general', 'Some fields are invalid.'),
                });
                break;
            case 'minimum':
                this.errors.push({
                    type: 'minimum',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.deposit-minimum-amount',
                        'Minimum deposit amount is £1'
                    ),
                });
                break;
            case 'limit-reached':
                this.errors.push({
                    type: 'limit-reached',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.deposit-limit-reached',
                        "Deposit can't exceed defined limit. You can review your limits [link]."
                    ),
                });
                break;
            case 'deposits-not-allowed':
                this.errors.push({
                    type: 'deposits-not-allowed',
                    message: this.languagesState.getTranslation(
                        'errors.unauthorized-client',
                        'Your account is suspended.'
                    ),
                });
                break;
            case 'limit-deposit-restriction':
                this.errors.push({
                    type: 'limit-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.limit-deposit-restriction',
                        "You're approaching your current net deposit limit! To adjust your limit, whether to increase or decrease it, please reach out to us at {email}. We're here to help!",
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'backoffice-deposit-restriction':
                this.errors.push({
                    type: 'backoffice-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.backoffice-deposit-restriction',
                        'Your deposits have been restricted. In case of any questions, please get in touch with us at {email}.',
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'failed-deposit-restriction':
                if (debugDetails === undefined) {
                    return;
                }
                this.errors.push({
                    type: 'failed-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.failed-deposit-restriction',
                        "We've noticed that there were unsuccessful deposit attempts on your account due to insufficient funds. We understand that managing finances can be challenging, and we want to ensure that your gambling experience remains safe and enjoyable. To give you some time to consider your gambling spend and review your finances we have placed deposit break on your account until {date}. If you would like to discuss any of our [responsibleGamblingTools], please reach out to us at {email}. We're here to help!",
                        {
                            email: this.accountHelperMail,
                            date: DateTime.from(debugDetails)?.format('DD-MM-YYYY HH:mm') ?? '',
                        }
                    ),
                });
                break;

            case 'kyc-deposit-restriction':
                this.errors.push({
                    type: 'failed-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.kyc-deposit-restriction',
                        'Your deposits are temporarily restricted until we receive your KYC information. Please check your email for further instructions and provide the necessary details. Once verified, the restriction will be lifted. For any questions, contact us at {email}.',
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'declined':
                this.errors.push({
                    type: 'declined',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.declined',
                        'Your deposit has been declined, please contact your bank for further information'
                    ),
                });
                break;
            case 'missing-fields':
                if (requiredFields === undefined) {
                    return;
                }

                this.errors.push({
                    type: 'missing-fields',
                    message: `Please add customer ${requiredFields.join(', ')} to customer 'Personal account' details.`,
                });
                break;
            default:
                this.errors.push({ type: 'unknown', message: `Unknown error happen - ${errorType ?? ''}` });
        }
    };
}
